import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import apiClient from '../apiClient';

interface BandFilterModalProps {
  show: boolean;
  setShow: (v: boolean) => void;
  exportType: 'cellular_csv' | 'wifi_csv';
  initialBandFilters: string[];
  onSubmit: (exportType: string, bandFilter: string[]) => void;
}

const WIFI_OPTIONS = ['2.4', '5', '6'];


export default function BandFilterModal({
  show,
  setShow,
  exportType,
  initialBandFilters,
  onSubmit,
}: BandFilterModalProps) {
  const [wifiSelected, setWifiSelected] = useState<string[]>([]);
  const [wifiAll, setWifiAll] = useState(false);
  const [lteAll, setLteAll] = useState(false);
  const [lteSelected, setLteSelected] = useState<string[]>([]);
  const [nrAll, setNrAll] = useState(false);
  const [nrSelected, setNrSelected] = useState<string[]>([]);
  const [lteBands, setLteBands] = useState<string[]>([]);
  const [nrBands, setNrBands] = useState<string[]>([]);

  const isWifi = exportType === 'wifi_csv';

  // Fetch LTE and NR bands for the cellular modal
  useEffect(() => {
    if (!isWifi && show && lteBands.length === 0) {
      apiClient.get('/sigcap/api/all-band-filter-list/')
        .then((res: { data: { lte: string[]; nr: string[] } }) => {
          setLteBands(res.data.lte ?? []);
          setNrBands(res.data.nr ?? []);
        })
        .catch((err: unknown) => console.error('Failed to fetch band list', err));
    }
  }, [show, isWifi, lteBands.length]);

  // Pre-check from sidebar selections when modal opens
  useEffect(() => {
    if (show) {
      if (isWifi) {
        const wifiValues = initialBandFilters
          .filter((b: string) => b.startsWith('wifi:'))
          .map((b: string) => b.split(':')[1]);
        setWifiAll(wifiValues.includes('all'));
        setWifiSelected(wifiValues.filter((v: string) => v !== 'all'));
      } else {
        const lteValues = initialBandFilters
          .filter((b: string) => b.startsWith('lte:'))
          .map((b: string) => b.split(':')[1]);
        setLteAll(lteValues.includes('all'));
        setLteSelected(lteValues.filter((v: string) => v !== 'all'));

        const nrValues = initialBandFilters
          .filter((b: string) => b.startsWith('nr:'))
          .map((b: string) => b.split(':')[1]);
        setNrAll(nrValues.includes('all'));
        setNrSelected(nrValues.filter((v: string) => v !== 'all'));
      }
    }
  }, [show, exportType, initialBandFilters]);

  const toggleWifi = (value: string) => {
    setWifiSelected((prev: string[]) =>
      prev.includes(value) ? prev.filter((v: string) => v !== value) : [...prev, value]
    );
  };

  const toggleLte = (value: string) => {
    setLteSelected((prev: string[]) =>
      prev.includes(value) ? prev.filter((v: string) => v !== value) : [...prev, value]
    );
  };

  const toggleNr = (value: string) => {
    setNrSelected((prev: string[]) =>
      prev.includes(value) ? prev.filter((v: string) => v !== value) : [...prev, value]
    );
  };

const handleSubmit = () => {
    setShow(false);
    if (isWifi) {
      const bandFilter: string[] = wifiAll
        ? ['wifi:all']
        : wifiSelected.map((b: string) => `wifi:${b}`);
      onSubmit(exportType, bandFilter);
      return;
    }

    // Build prefixed band_filter for the backend.
    // 'lte:all' / 'nr:all' → include all of that technology, exclude the other.
    // Specific 'lte:X' / 'nr:X' → filter to those bands only.
    // No entries at all → backend includes everything (empty list = no filter).
    const bandFilter: string[] = [];

    if (lteAll) {
      bandFilter.push('lte:all');
    } else {
      lteSelected.forEach((b: string) => bandFilter.push(`lte:${b}`));
    }

    if (nrAll) {
      bandFilter.push('nr:all');
    } else {
      nrSelected.forEach((b: string) => bandFilter.push(`nr:${b}`));
    }

    onSubmit(exportType, bandFilter);
  };

  const title = isWifi ? 'Wi-Fi CSV Band Filter' : 'Cellular CSV Band Filter';

  return (
    <Modal show={show} onHide={() => setShow(false)} centered={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Text className="text-muted d-block mb-2">
          Select bands to include. Leave all unchecked to export all bands.
        </Form.Text>

        {isWifi ? (
          <>
            <Form.Check
              type="checkbox"
              id="band-filter-wifi-all"
              label={<strong>Wi-Fi (all bands)</strong>}
              checked={wifiAll}
              onChange={() => {
                setWifiAll((prev: boolean) => !prev);
                if (!wifiAll) setWifiSelected([]);
              }}
            />
            {!wifiAll && (
              <div className="ms-3 mt-1">
                {WIFI_OPTIONS.map(item => (
                  <Form.Check
                    key={item}
                    type="checkbox"
                    id={`band-filter-wifi-${item}`}
                    label={`${item} GHz`}
                    checked={wifiSelected.includes(item)}
                    onChange={() => toggleWifi(item)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="d-flex gap-4">
            {/* LTE 4G section */}
            <div className="flex-fill">
              <Form.Check
                type="checkbox"
                id="band-filter-lte-all"
                label={<strong>LTE 4G (all bands)</strong>}
                checked={lteAll}
                onChange={() => {
                  setLteAll((prev: boolean) => !prev);
                  if (!lteAll) setLteSelected([]);
                }}
              />
              {!lteAll && (
                <div className="ms-3 mt-1" style={{ columns: 2 }}>
                  {lteBands.map((band: string) => (
                    <Form.Check
                      key={`lte-${band}`}
                      type="checkbox"
                      id={`band-filter-lte-${band}`}
                      label={`Band ${band}`}
                      checked={lteSelected.includes(band)}
                      onChange={() => toggleLte(band)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* NR 5G section */}
            <div className="flex-fill">
              {/* "All NR 5G" header toggle */}
              <Form.Check
                type="checkbox"
                id="band-filter-nr-all"
                label={<strong>NR 5G (all bands)</strong>}
                checked={nrAll}
                onChange={() => {
                  setNrAll((prev: boolean) => !prev);
                  if (!nrAll) setNrSelected([]);
                }}
              />
              {/* Individual NR bands, hidden when "all" is selected */}
              {!nrAll && (
                <div className="ms-3 mt-1" style={{ columns: 3 }}>
                  {nrBands.map((band: string) => (
                    <Form.Check
                      key={`nr-${band}`}
                      type="checkbox"
                      id={`band-filter-nr-${band}`}
                      label={`n${band}`}
                      checked={nrSelected.includes(band)}
                      onChange={() => toggleNr(band)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit}>Download</Button>
      </Modal.Footer>
    </Modal>
  );
}
